import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthContext } from '../../../contexts/user-context/user-context';
import MobileDataCard, { RowConfig } from '../mobile-data-card/mobile-data-card';
import { CenteredTableCell } from '../generic-table/utils';
import { Alert } from '@mui/material';

export interface MobileCardListProps<T extends object> {
    items: T[];
    rowConfigs: RowConfig<T>[];
    isLoading: boolean;
    selectedItems: Map<string, T>;
    updateSelectedItems?: (updater: (draft: Map<string, T>) => void) => void;
    getItemUID: (item: T) => string;
    rowStyles?: (item: T) => React.CSSProperties;
    selectionEnabled?: boolean;
    cardIcon?: (item: T) => JSX.Element | undefined;
    onIconClick?: (item: T) => void;
    iconBoxStyles?: (item: T) => React.CSSProperties;
    cardFooter?: (item: T) => { content: JSX.Element, background: string };
    footerBackgroundColor?: string;
    onCardClick?: (item: T) => void;
}

export default function MobileCardList<T extends object>(listProps: MobileCardListProps<T>) {
    const { onCardClick, cardIcon, iconBoxStyles, onIconClick, selectionEnabled, items, rowConfigs, isLoading, selectedItems, updateSelectedItems, getItemUID, rowStyles, cardFooter, footerBackgroundColor } = { ...listProps };

    const toggleIdSelection = (item: T) => {
        if (updateSelectedItems)
            updateSelectedItems((draft) => {
                const rowId = getItemUID(item);
                if (draft.has(rowId)) draft.delete(rowId);
                else draft.set(rowId, item);
            });
    };

    const selectAllCards = (items: T[]) => {
        if (updateSelectedItems)
            updateSelectedItems((draft) => {
                items.forEach((item) => draft.set(getItemUID(item), item));
            });
    };

    const unSelectAllCards = (items: T[]) => {
        if (updateSelectedItems)
            updateSelectedItems((draft) => {
                items.forEach((item) => draft.delete(getItemUID(item)));
            });
    };

    const currentIds = items.map((item) => getItemUID(item));

    const numSelected = currentIds.reduce(
        (result: number, itemID: string) => (selectedItems.has(itemID) ? result + 1 : result),
        0
    );
    const cardCount = items.length;
    const allSelected = numSelected === cardCount;

    return (
        <Box sx={{ padding: 0, paddingBottom: 1 }}>
            {isLoading ? (
                <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress />
                </div>
            ) : <>{

                items.length === 0 ? <Alert severity="info">No items to show!</Alert> :
                    <>
                        {selectionEnabled && items.length > 0 && (
                            <div >
                                <Checkbox
                                    color="primary"
                                    indeterminate={numSelected > 0 && numSelected < cardCount}
                                    checked={(allSelected || (cardCount > 0 && numSelected === cardCount)) && numSelected > 0}
                                    onChange={() => {
                                        if (allSelected) unSelectAllCards(items);
                                        else selectAllCards(items);
                                    }}
                                /> Select All
                            </div>
                        )}
                        {items.map(item => (
                            <MobileDataCard<T>
                                id={getItemUID(item)}
                                data={item}
                                key={getItemUID(item)}
                                rowConfigs={rowConfigs}
                                toggleIdSelection={toggleIdSelection}
                                onCardClick={onCardClick}
                                onIconClick={onIconClick}
                                selectionEnabled={selectionEnabled}
                                cardIcon={cardIcon && cardIcon(item)}
                                iconBoxStyle={iconBoxStyles && iconBoxStyles(item)}
                                footer={cardFooter && cardFooter(item)}
                                selectedItems={selectedItems}
                                />
                        ))}

                    </>

            }</>}
        </Box >
    );
}
